import { AuthContext } from "@/context/AuthContext";
import ConductionAPI from "@/javascript/conduction-api";
import { ProjectSubmission } from "@/javascript/project-objects";
import { Classroom } from "@/javascript/user-objects";
import { Utils } from "@/javascript/utils";
import {
    Badge,
    Button,
    Flex,
    Pagination,
    ScrollArea,
    Skeleton,
    Table,
    Text,
} from "@mantine/core";
import { IconAlertCircle, IconCheck } from "@tabler/icons-react";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import ConductionModal from "../ConductionModal";

interface ProjectSubmissionsProps {
    onClose: () => void;
    classroom?: Classroom;
}

const ProjectSubmissions: React.FC<ProjectSubmissionsProps> = ({ onClose, classroom }) => {
    const [projectSubmissions, setProjectSubmissions] = useState<ProjectSubmission[] | null>(null);
    const [desiredPage, setDesiredPage] = useState<number>(1);
    const [submissionsPerPage, setSubmissionsPerPage] = useState<number>(6);
    const [loading, setLoading] = useState<boolean>(true);

    const auth = useContext(AuthContext);
    if (!auth) {
        throw new Error("ProjectSubmissions must be a child of AuthContext.");
    }
    const token = auth.token;
    if (!token) return null;

    // Set view height to 60vh as in ProjectSelect
    const height = 60; // (vh)

    // Calculate submissions per page using a similar formula as ProjectSelect.
    const calculateSubmissionsPerPage = useCallback(() => {
        const rowHeight = 45; // pixel estimation
        const containerHeight = window.innerHeight * (height / 100) - 100; // 60% of viewport height minus an offset
        setSubmissionsPerPage(Math.floor(containerHeight / rowHeight));

        if (
            projectSubmissions &&
            Math.floor(containerHeight / rowHeight) * (desiredPage - 1) + 1 > projectSubmissions.length
        ) {
            setDesiredPage(1);
        }
    }, [desiredPage, projectSubmissions]);

    useEffect(() => {
        calculateSubmissionsPerPage();
        window.addEventListener("resize", calculateSubmissionsPerPage);
        return () => window.removeEventListener("resize", calculateSubmissionsPerPage);
    }, [calculateSubmissionsPerPage]);

    // Load project submissions using the token from auth.
    useEffect(() => {
        const notificationId = Utils.displayNotification(
            "Loading Project Submissions",
            "Fetching submitted projects, please wait...",
            "blue",
            "loading"
        );

        (classroom ?
            ConductionAPI.getClassroomProjectSubmissions(classroom.id, token)
            :
            ConductionAPI.getProjectSubmissions(token)
        ).then((loadedProjectSubmissions: ProjectSubmission[] | null) => {
            if (!loadedProjectSubmissions || loadedProjectSubmissions.length === 0) {
                setProjectSubmissions(null);
                Utils.updateNotification(
                    notificationId,
                    "No Project Submissions Found",
                    "No project submissions are currently available.",
                    "red",
                    <IconAlertCircle size={16} />,
                    true
                );
            } else {
                setProjectSubmissions(
                    loadedProjectSubmissions.sort(
                        (a, b) =>
                            new Date(b.dateSubmitted).getTime() - new Date(a.dateSubmitted).getTime()
                    )
                );
                Utils.updateNotification(
                    notificationId,
                    "Project Submissions Loaded",
                    undefined,
                    "green",
                    <IconCheck size={16} />
                );
            }
        })
            .catch(() => {
                Utils.updateNotification(
                    notificationId,
                    "Error Fetching Submissions",
                    "An error occurred while retrieving project submissions.",
                    "red",
                    <IconAlertCircle size={16} />,
                    true
                );
            })
            .finally(() => setLoading(false));
    }, [classroom?.id, token]);

    // Paginate the submissions
    const paginatedSubmissions = useMemo(() => {
        const start = (desiredPage - 1) * submissionsPerPage;
        return projectSubmissions?.slice(start, start + submissionsPerPage) ?? [];
    }, [desiredPage, projectSubmissions, submissionsPerPage]);

    // Helper: format the submitted date
    const formatDateString = (stringDate: Date): string => {
        if (!stringDate) return "Invalid Date";
        const date = new Date(stringDate + " UTC");
        return `${date.toLocaleDateString()} at ${date.toLocaleTimeString().split(" ")[0]}`;
    };

    return (
        <ConductionModal
            title="Project Submissions"
            onClose={onClose}
            size={"lg"}
        >
            <Flex direction="column" gap="md">
                {loading ? (
                    <Skeleton height={200} radius="md" />
                ) : projectSubmissions === null ? (
                    <Text>No project submissions found.</Text>
                ) : (
                    <ScrollArea style={{ width: "100%" }}>
                        <Table highlightOnHover>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>User</Table.Th>
                                    <Table.Th>Submitted</Table.Th>
                                    <Table.Th>Actions</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {paginatedSubmissions.map((submission) => (
                                    <Table.Tr key={submission.projectId}>
                                        <Table.Td>{submission.projectOwner?.username}</Table.Td>
                                        <Table.Td><Badge color="blue">{formatDateString(submission.dateSubmitted)}</Badge></Table.Td>
                                        <Table.Td>
                                            <Button
                                                size="xs"
                                                onClick={() =>
                                                    auth.openProject(submission.projectId)
                                                }
                                            >
                                                Load Project
                                            </Button>
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    </ScrollArea>
                )}

                {projectSubmissions && projectSubmissions.length > submissionsPerPage && (
                    <Pagination
                        total={Math.ceil(projectSubmissions.length / submissionsPerPage)}
                        value={desiredPage}
                        onChange={setDesiredPage}
                        style={{ paddingTop: 20 }}
                    />
                )}
            </Flex>
        </ConductionModal>
    );
};

export default ProjectSubmissions;
